import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, Mic, Phone, MapPin } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useSite } from "@/context/ThemeContext";

export function Footer() {
  const { data } = useSite();
  const socials = data?.content?.socials || {};
  const categories = data?.categories || [];

  return (
    <footer className="relative z-10 border-t border-c bg-surface/40 mt-24" data-testid="footer">
      <div className="mx-auto max-w-7xl px-5 py-16 grid gap-10 md:grid-cols-12">
        <div className="md:col-span-4">
          <Logo showTagline />
          <p className="text-muted-c text-sm mt-5 max-w-xs leading-relaxed">
            Roha's trusted computer institute since 1999. Practical, personal and future-ready learning.
          </p>
          <div className="flex gap-3 mt-6">
            {socials.facebook && (
              <a href={socials.facebook} target="_blank" rel="noreferrer" className="p-2.5 rounded-full btn-ghost" data-testid="social-facebook"><Facebook size={18} /></a>
            )}
            <a href={socials.instagram || "#"} target="_blank" rel="noreferrer" className="p-2.5 rounded-full btn-ghost" data-testid="social-instagram"><Instagram size={18} /></a>
            <a href={socials.youtube || "#"} target="_blank" rel="noreferrer" className="p-2.5 rounded-full btn-ghost" data-testid="social-youtube"><Youtube size={18} /></a>
            <a href={socials.speakease || "https://speakease.in"} target="_blank" rel="noreferrer" className="p-2.5 rounded-full btn-ghost" data-testid="social-speakease"><Mic size={18} /></a>
          </div>
        </div>

        <div className="md:col-span-2">
          <h4 className="text-sm uppercase tracking-widest text-primary-c mb-4 font-semibold">Explore</h4>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/" className="text-muted-c hover:text-primary-c transition-colors">Home</Link></li>
            <li><Link to="/about" className="text-muted-c hover:text-primary-c transition-colors">About</Link></li>
            <li><Link to="/courses" className="text-muted-c hover:text-primary-c transition-colors">Courses</Link></li>
            <li><Link to="/activities" className="text-muted-c hover:text-primary-c transition-colors">Student Activities</Link></li>
            <li><Link to="/gallery" className="text-muted-c hover:text-primary-c transition-colors">Gallery</Link></li>
            <li><Link to="/achievements" className="text-muted-c hover:text-primary-c transition-colors">Achievements</Link></li>
            <li><Link to="/events" className="text-muted-c hover:text-primary-c transition-colors">Events</Link></li>
            <li><Link to="/admission" className="text-muted-c hover:text-primary-c transition-colors">Admission</Link></li>
            <li><Link to="/contact" className="text-muted-c hover:text-primary-c transition-colors">Contact</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-sm uppercase tracking-widest text-primary-c mb-4 font-semibold">Categories</h4>
          <ul className="space-y-2.5 text-sm">
            {categories.map((c) => (
              <li key={c.id}>
                <Link to={`/courses/${c.id}`} className="text-muted-c hover:text-primary-c transition-colors">{c.name}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-sm uppercase tracking-widest text-primary-c mb-4 font-semibold">Reach Us</h4>
          <a href="tel:9272980098" className="block text-t font-semibold mb-3" data-testid="footer-phone">To reach us call / msg @ <span className="text-primary-c">9272980098</span></a>
          <div className="text-sm text-muted-c space-y-3">
            <div>
              <div className="text-t font-semibold flex items-center gap-1.5"><MapPin size={14} className="text-primary-c" /> Roha Centre</div>
              <a href="https://maps.app.goo.gl/HACTGTnGkmz8Fgj27" target="_blank" rel="noreferrer" className="text-muted-c text-xs hover:text-primary-c" data-testid="footer-map-roha">Nirmaan Plaza, Roha ST Bus Stand</a>
            </div>
            <div>
              <div className="text-t font-semibold flex items-center gap-1.5"><MapPin size={14} className="text-primary-c" /> Kolad Centre</div>
              <a href="https://maps.app.goo.gl/io8dihn75pKeYXDi9" target="_blank" rel="noreferrer" className="text-muted-c text-xs hover:text-primary-c" data-testid="footer-map-kolad">Kadam Apartment, Ambewadi, Kolad</a>
            </div>
            <a href="https://speakease.in" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-primary-c font-semibold"><Mic size={16} /> Try SpeakEase</a>
          </div>
        </div>
      </div>
      <div className="border-t border-c py-6 px-5 text-center text-xs text-muted-c space-y-2">
        <p className="max-w-4xl mx-auto leading-relaxed">
          Bits &amp; Bytes Computer Institute · Established 1999 · Roha &amp; Kolad, Raigad, Maharashtra · Training for Change · ISO 21001:2018 Certified · Just Dial Verified · MS-CIT · GCC-TBC · Tally Prime · Advanced Excel · MS Office · Coding · AI · Robotics · IoT · 3D Printing · VR · English Speaking · Computer classes &amp; training in Roha, Raigad.
        </p>
        <p>© {new Date().getFullYear()} Bits &amp; Bytes Computer Institute. All rights reserved. · <Link to="/admin" className="hover:text-primary-c" data-testid="footer-admin-link">Admin Login</Link></p>
      </div>
    </footer>
  );
}
